import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { fetchMe, listAccounts, logout, type MailAccount } from '../lib/api'
import { useSession } from '../lib/useSession'
import { Icon } from './Icon'
import { UserAvatar } from './UserAvatar'

type Props = {
  open: boolean
  onClose: () => void
  avatarRevision?: number
  onOpenProfile: () => void
  onOpenSettings: () => void
  onOpenAccount: (accountId: string) => void
  anchorRef: React.RefObject<HTMLElement | null>
}

export function UserMenu({
  open,
  onClose,
  avatarRevision = 0,
  onOpenProfile,
  onOpenSettings,
  onOpenAccount,
  anchorRef,
}: Props) {
  const { t } = useTranslation()
  const { user, amr } = useSession()
  const [accountsOpen, setAccountsOpen] = useState(false)
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({})
  const panelRef = useRef<HTMLDivElement | null>(null)

  const updateMenuPosition = useCallback(() => {
    const anchor = anchorRef.current
    const panel = panelRef.current
    if (!anchor || !panel) return

    if (window.innerWidth <= 759) {
      setMenuStyle({})
      return
    }

    const rect = anchor.getBoundingClientRect()
    const gap = 10
    const menuWidth = Math.min(300, window.innerWidth - 80)
    const menuHeight = panel.offsetHeight

    let left = rect.right + gap
    let top = rect.bottom - menuHeight

    if (left + menuWidth > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - menuWidth - 8)
    }
    if (left < 8) left = 8
    if (top < 8) top = 8
    if (top + menuHeight > window.innerHeight - 8) {
      top = Math.max(8, window.innerHeight - menuHeight - 8)
    }

    setMenuStyle({ position: 'fixed', left, top, width: menuWidth })
  }, [anchorRef])

  const accountsQ = useQuery({
    queryKey: ['accounts'],
    queryFn: listAccounts,
    enabled: open,
  })
  const meQ = useQuery({
    queryKey: ['auth-me'],
    queryFn: fetchMe,
    enabled: open,
  })
  const accounts: MailAccount[] = accountsQ.data?.items || []
  const passkeyOk =
    (meQ.data?.passkey_count || 0) > 0 || (amr || []).includes('passkey') || (amr || []).includes('recovery')

  useEffect(() => {
    if (!open) return
    function onDoc(e: MouseEvent) {
      const tEl = e.target as Node
      if (panelRef.current?.contains(tEl)) return
      if (anchorRef.current?.contains(tEl)) return
      onClose()
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, onClose, anchorRef])

  useLayoutEffect(() => {
    if (!open) return
    updateMenuPosition()
    window.addEventListener('resize', updateMenuPosition)
    window.addEventListener('scroll', updateMenuPosition, true)
    return () => {
      window.removeEventListener('resize', updateMenuPosition)
      window.removeEventListener('scroll', updateMenuPosition, true)
    }
  }, [open, updateMenuPosition, accountsOpen])

  if (!open) return null

  const menuFixed = window.innerWidth > 759

  const display =
    (meQ.data?.display_name || user?.display_name || '').trim() ||
    (user?.email || meQ.data?.email || '').split('@')[0] ||
    t('profile.you')

  async function onLogout() {
    onClose()
    await logout()
    window.location.assign('/welcome')
  }

  return (
    <>
      <div className="rd-user-menu-backdrop" aria-hidden onClick={onClose} />
      <div
        ref={panelRef}
        className={`rd-user-menu${menuFixed ? ' rd-user-menu-fixed' : ''}`}
        style={menuFixed ? menuStyle : undefined}
        role="menu"
        aria-label={t('profile.menuAria')}
      >
        <div className="rd-user-menu-head">
          <UserAvatar size={44} revision={avatarRevision} />
          <div className="rd-user-menu-meta">
            <strong>{display}</strong>
            <span>{user?.email || meQ.data?.email || '—'}</span>
            <span className="rd-user-menu-passkey">
              {passkeyOk ? (
                <>
                  passkey <span aria-hidden>✓</span>
                </>
              ) : (
                t('profile.passkeyNone')
              )}
            </span>
          </div>
        </div>

        <button
          type="button"
          className="rd-user-menu-item"
          role="menuitem"
          onClick={() => {
            onClose()
            onOpenProfile()
          }}
        >
          <Icon name="person" size={18} /> {t('profile.title')}
        </button>
        <button
          type="button"
          className="rd-user-menu-item"
          role="menuitem"
          onClick={() => {
            onClose()
            onOpenSettings()
          }}
        >
          <Icon name="settings" size={18} /> {t('profile.systemSettings')}
        </button>

        <div className="rd-user-menu-acc">
          <button
            type="button"
            className={`rd-user-menu-item rd-user-menu-acc-sum${accountsOpen ? ' open' : ''}`}
            aria-expanded={accountsOpen}
            onClick={() => setAccountsOpen((v) => !v)}
          >
            <Icon name="manage_accounts" size={18} />
            <span style={{ flex: 1 }}>{t('profile.importedAccounts')}</span>
            <Icon name="expand_more" size={18} />
          </button>
          {accountsOpen ? (
            <div className="rd-user-menu-acc-body">
              {accounts.length === 0 ? (
                <p className="rd-user-menu-empty">{t('settings.noAccounts')}</p>
              ) : (
                accounts.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    className="rd-user-menu-item nested"
                    onClick={() => {
                      onClose()
                      onOpenAccount(a.id)
                    }}
                  >
                    <span className="rd-user-menu-acct-label">{a.label || a.username}</span>
                    <span className="rd-user-menu-acct-email">{a.username}</span>
                  </button>
                ))
              )}
            </div>
          ) : null}
        </div>

        <button
          type="button"
          className="rd-user-menu-item danger"
          role="menuitem"
          onClick={() => void onLogout()}
        >
          <Icon name="logout" size={18} /> {t('auth.logout')}
        </button>
      </div>
    </>
  )
}
